/**
 * Format a duration in minutes to a human-readable string.
 * Examples: 45 → "45m", 90 → "1h 30m", 120 → "2h", null → null
 */
export function formatMinutes(minutes: number | null | undefined): string | null {
  if (minutes === null || minutes === undefined) return null;
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ── Date utilities ───────────────────────────────────────────────────────────

/** Today's date as YYYY-MM-DD in the local timezone. */
export function getLocalDateStr(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Convert an ISO / date string (or a Date) to YYYY-MM-DD in the local timezone. */
export function toLocalDateStr(date: string | Date): string {
  const dt = new Date(date);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** The local UTC offset in force at `dt`, as `+HH:MM` / `-HH:MM`. */
export function localOffsetSuffix(dt: Date): string {
  const offset = dt.getTimezoneOffset();
  const sign = offset <= 0 ? '+' : '-';
  const absOff = Math.abs(offset);
  const hh = String(Math.floor(absOff / 60)).padStart(2, '0');
  const mm = String(absOff % 60).padStart(2, '0');
  return `${sign}${hh}:${mm}`;
}

/**
 * An ISO-8601 instant on `dt`'s LOCAL calendar day at wall-clock `time`
 * (`HH:MM`), with the local offset spelled out: `YYYY-MM-DDTHH:MM:00+HH:MM`.
 *
 * The date must come from the local getters, never `toISOString()`: that is
 * the UTC calendar date, and pairing it with a local offset is off by one day
 * for anyone whose evening (west of UTC) or early morning (east of UTC) falls
 * on a different UTC date.
 */
export function toOffsetIso(dt: Date, time: string): string {
  return `${toLocalDateStr(dt)}T${time}:00${localOffsetSuffix(dt)}`;
}

/** True when `dateStr` represents today in the local timezone. */
export function isToday(dateStr: string | null): boolean {
  if (!dateStr) return false;
  return toLocalDateStr(dateStr) === getLocalDateStr();
}

/** True when `dateStr` is strictly after today in the local timezone. */
export function isUpcoming(dateStr: string | null): boolean {
  if (!dateStr) return false;
  return toLocalDateStr(dateStr) > getLocalDateStr();
}

/** True when `dateStr` is strictly before today in the local timezone. */
export function isPast(dateStr: string | null): boolean {
  if (!dateStr) return false;
  return toLocalDateStr(dateStr) < getLocalDateStr();
}

/** True when `dateStr` is tomorrow in the local timezone. */
export function isTomorrow(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const y = tomorrow.getFullYear();
  const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
  const d = String(tomorrow.getDate()).padStart(2, '0');
  return toLocalDateStr(dateStr) === `${y}-${m}-${d}`;
}

/**
 * True when `dateStr` falls between day-after-tomorrow and end of the current
 * week (Saturday), inclusive. Week runs Sunday–Saturday.
 */
export function isThisWeekRemaining(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const localDate = toLocalDateStr(dateStr);
  const today = new Date();
  const dayAfterTomorrow = new Date(today);
  dayAfterTomorrow.setDate(today.getDate() + 2);
  // Saturday = end of week (day 6). If today is Saturday (6), no remaining days.
  const dayOfWeek = today.getDay(); // 0=Sun
  const daysUntilSaturday = 6 - dayOfWeek;
  if (daysUntilSaturday < 2) return false; // tomorrow or today is already Fri/Sat
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + daysUntilSaturday);
  const fmt = (dt: Date) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };
  return localDate >= fmt(dayAfterTomorrow) && localDate <= fmt(endOfWeek);
}

/** True when `dateStr` is after the current week's Saturday (Sun–Sat week). */
export function isAfterThisWeek(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0=Sun
  const daysUntilSaturday = 6 - dayOfWeek;
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + daysUntilSaturday);
  const y = endOfWeek.getFullYear();
  const m = String(endOfWeek.getMonth() + 1).padStart(2, '0');
  const d = String(endOfWeek.getDate()).padStart(2, '0');
  return toLocalDateStr(dateStr) > `${y}-${m}-${d}`;
}

/**
 * Format a YYYY-MM-DD date for display with today/tomorrow handling.
 * Returns "Today", "Tomorrow", or "Mon, Jan 1".
 */
export function formatDisplayDate(dateStr: string): string {
  try {
    const parts = dateStr.split('-');
    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (d.getTime() === today.getTime()) return 'Today';
    if (d.getTime() === tomorrow.getTime()) return 'Tomorrow';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

/**
 * Format a YYYY-MM-DD date as "Jan 1, 2026" (no today/tomorrow).
 * Used on goal-create screens where relative labels aren't useful.
 */
export function formatDisplayDateLong(dateStr: string): string {
  try {
    const parts = dateStr.split('-');
    const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

/** Format a date string as "Jan 1" or return null. */
export function formatShortDate(dateStr: string | null): string | null {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

/** Format a date string as "Mon, Jan 1" (weekday + short date). */
export function formatWeekdayDate(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}
